import React, { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-apollo-hooks'
import styled from 'react-emotion'
import NumberFormat from 'react-number-format'
import { Loader, Svg } from '../application'
import { TextErrors } from '../notification'
import { urls } from '../resources'
import { Container, colors } from '../styles'
import * as Mutations from './mutations'
import * as Queries from './queries'
import getText, { getTextErrors } from './strings'

const ModualContainer = styled('div')`
  margin: 0 10px 20px 10px;
`

const Message = styled('div')`
    border-radius: 4px;
    border: 1px solid #555;
    padding: 20px 0 15px 20px;
    margin: 20px 0; 
    background-color: #e5ffdd;   
`

const Error = styled('div')`
    border-radius: 4px;
    border: 1px solid red;
    padding: 20px 0 15px 20px; 
    margin: 20px 0;   
    background-color: #fdd;
`

const Header = styled('div')`
    margin: 20px 0 10px 0;

    .pictogram {
      height: 50px;
      width: 50px;
    }
`

const Person = styled('div')`
    margin: 20px 0;
`
const Name = styled('div')`
  font-size: 1.5em;
`

const Fields = styled('div')`
    display: flex;
    flex-wrap: wrap;    
    justify-content: space-between;
    label {
        margin-bottom: 0;
    }
    >div {
        width: calc(33% - 10px);        
        margin-bottom: 10px;
        input, select {
            width: 100%;
        }
        @media(max-width: 767px) {
            width: 100%;        
        }
    }
`

const ButtonRow = styled('div')`
    display: flex;    
`

const FormContainer = styled('div')`
    input, select {
        height: 36px;
        ${props => props.valid ? '' : `border: 1px solid ${colors.tui_red}`}
    }    
    span {
        ${props => props.valid ? '' : `color: ${colors.tui_red}`}
    }
`

const Overview = ({ match }) => {
  const bookingNumber = match.params.bookingNumber

  const [isModified, setIsModified] = useState(false)
  const [hasValidationErrors, setHasValidationErrors] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)
  const [atiForm, setAtiForm] = useState(null)

  const { loading, data, error: atiError } = useQuery(Queries.getAti(bookingNumber))


  useEffect(() => {
    setError(!!atiError)
    if (data) {
      setAtiForm(data.ati)
    }
  }, [data, atiError])

  const updateAti = useMutation(Mutations.updateAti, {
    update: (proxy, mutationResult) => {
      if (mutationResult.data.updateAti) {
        setSaving(false)
        setIsSaved(mutationResult.data.updateAti.UpdatedOk)
        setHasValidationErrors(!mutationResult.data.updateAti.UpdatedOk)
        setAtiForm(mutationResult.data.updateAti.ReservationAtiInformation)
        setIsModified(false)
      }
    }
  })

  const getDateMask = (label) => {
    if(label.indexOf('åååå-mm-dd') !== -1) {
      return '####-##-##'
    }
    return '##-##-####'
  }

  const handleClick = () => {
    window.scrollTo(0, 0)
    setSaving(true)
    setIsSaved(false)
    setError(false)
    setHasValidationErrors(false)

    updateAti({
      variables: {
        bookingNumber,
        data:
        {
          ReservationId: atiForm.ReservationId,
          Persons: atiForm.Persons.map(p => ({
            ReservationPersonId: p.ReservationPersonId,
            Values: p.Values.map(v => ({
              Key: v.Key,
              Value: v.Value
            }))
          }))
        }
      }
    })
  }

  const createComponent = (personId, data) => {
    switch (data.Type) {
      case "NATIONALITY":
        return (
          <FormContainer valid={data.ValidationMessageLocalized == null}>
            <label>{data.KeyLocalized}</label>
            <select defaultValue={data.Value} onChange={(e) => updateValue(personId, data.FieldId, e.target.value)}>
              {atiForm.Nationalities.map(n => (<option key={n.Code} value={n.Code}>{n.Name}</option>))}
            </select>
            {data.ValidationMessageLocalized && <span>{data.ValidationMessageLocalized}</span>}
          </FormContainer>
        )
      case "GENDER":
        if (data.Value === 'I')
          return (
            <FormContainer valid={data.ValidationMessageLocalized == null}>
              <label>{data.KeyLocalized}</label>
              <input type="text" value={getText('gender', 'infant')} readOnly={true} />
              {data.ValidationMessageLocalized && <span>{data.ValidationMessageLocalized}</span>}
            </FormContainer>
          )
        if (data.Value === 'C')
          return (
            <FormContainer valid={data.ValidationMessageLocalized == null}>
              <label>{data.KeyLocalized}</label>
              <input type="text" value={getText('gender', 'child')} readOnly={true} />
              {data.ValidationMessageLocalized && <span>{data.ValidationMessageLocalized}</span>}
            </FormContainer>
          )
        return (
          <FormContainer valid={data.ValidationMessageLocalized == null}>
            <label>{data.KeyLocalized}</label>
            <select defaultValue={data.Value} onChange={(e) => updateValue(personId, data.FieldId, e.target.value)}>
              <option key="" value="">{getText('gender', 'select')}</option>
              <option key="F" value="F">{getText('gender', 'female')}</option>
              <option key="M" value="M">{getText('gender', 'male')}</option>
            </select>
            {data.ValidationMessageLocalized && <span>{data.ValidationMessageLocalized}</span>}
          </FormContainer>
        )
      case "DATE":
        return (
          <FormContainer valid={data.ValidationMessageLocalized == null}>
          <label>{data.KeyLocalized}</label>
          <NumberFormat format={getDateMask(data.KeyLocalized)} mask="" type="text" value={data.Value || ''} onChange={(e) => updateValue(personId, data.FieldId, e.target.value)} />
          {data.ValidationMessageLocalized && <span>{data.ValidationMessageLocalized}</span>}
        </FormContainer>
      )
      case "STRING":
      default:
        return (
          <FormContainer valid={data.ValidationMessageLocalized == null}>
            <label>{data.KeyLocalized}</label>
            < input type="text" value={data.Value || ''} onChange={(e) => updateValue(personId, data.FieldId, e.target.value)} />
            {data.ValidationMessageLocalized && <span>{data.ValidationMessageLocalized}</span>}
          </FormContainer>
        )

    }
  }

  const updateValue = (personId, fieldId, value) => {
    const clone = { ...atiForm }
    clone.Persons.filter(p => p.ReservationPersonId === personId)[0].Values.filter(p => p.FieldId === fieldId)[0].Value = value
    setAtiForm(clone)
    setIsModified(true)
  }

  if (loading)
    return <Container><Loader /></Container>


  return (
    <Container>
      <ModualContainer>
        <Header>
          <Svg icon="tui-smile" />
        </Header>
        <TextErrors textErrors={getTextErrors()} />
        {saving && <Message><h3>{getText('saving')}</h3></Message>}
        {isSaved && <Message>
          <h3>{getText('saved')}</h3>
          <div>{getText('savedText')}</div>
        </Message>}
        {hasValidationErrors && <Error><h4>{getText('validationError')}</h4></Error>}
        {(error) && <Error><h3>{getText('generalError')}</h3></Error>}
        <Header>
          <h2>{getText('title')}</h2>
          <div><strong>{getText('description1')}</strong></div>
          <div>
            <a href={urls.passportAndVisa}>{getText('linkText')}</a>&nbsp;
                    {getText('description2')}
          </div>
          <div><strong>{getText('description3')}&nbsp;
            <a href={urls.passportAndVisa2} target="_blank" rel="noopener noreferrer">{getText('linkText2')}</a></strong>
          </div>
        </Header>
        <h2>{bookingNumber}</h2>
        {atiForm && atiForm.Persons.length > 0 && (
          atiForm.Persons.map(p => (
            <Person key={p.ReservationPersonId}>
              <Name>{p.DisplayName}</Name>
              <Fields>
                {p.Values.map(v => (
                  <div key={v.FieldId}>
                    {createComponent(p.ReservationPersonId, v)}
                  </div>
                ))}
              </Fields>
            </Person>
          )
          )
        )}
        <ButtonRow>
          {atiForm && <button className="btn btn-function" onClick={() => handleClick()}>{getText('save')}</button>}
        </ButtonRow>
      </ModualContainer>
    </Container>
  )
}

export default Overview